import {
  Dropdown,
  Input,
  Option,
  Tag,
  TagPicker,
  TagPickerControl,
  TagPickerGroup,
  TagPickerInput,
  TagPickerList,
  TagPickerOption,
} from "@fluentui/react-components"
import type { Page, ProblemDetails, Scope } from "@rema/shared"
import type {
  ApiClient,
  ApiClientStatus,
  CreateClientRequest,
  CreateClientResponse,
  UpdateClientRequest,
} from "@rema/shared/schemas/clients"
import type { DomainPage } from "@rema/shared/schemas/domains"
import { useQuery } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useSession } from "../../auth/session"
import { AppDrawerForm } from "../../components/app-drawer-form"
import { FormField } from "../../components/form-field"
import { ApiError, api } from "../../lib/api"
import { applyFieldErrors, problemMessage } from "../../lib/problem"
import { clientsI18n as i18n } from "./i18n"

// Valor sentinela de la opción "sin coincidencias" del selector de scopes.
const NO_MATCHES = "__no_matches__"

interface ClientFormValues {
  domainId: string
  name: string
  scopes: string[]
  status: ApiClientStatus
}

function defaultsFor(client: ApiClient | null): ClientFormValues {
  return {
    domainId: client?.domain_id ?? "",
    name: client?.name ?? "",
    scopes: client?.scopes ?? [],
    status: client?.status ?? "active",
  }
}

export interface ClientFormDrawerProps {
  open: boolean
  client: ApiClient | null
  onClose: () => void
  onCreate: (input: CreateClientRequest) => Promise<CreateClientResponse>
  onUpdate: (id: string, input: UpdateClientRequest) => Promise<unknown>
}

export const ClientFormDrawer = ({
  open,
  client,
  onClose,
  onCreate,
  onUpdate,
}: ClientFormDrawerProps) => {
  const isEdit = client !== null
  // Solo el admin de instancia puede crear clients sin dominio (de instancia, acceso a
  // todos los dominios); un maestro de dominio queda acotado al suyo.
  const { user } = useSession()
  const isInstanceAdmin = user?.domain === "system"
  const { control, handleSubmit, reset, setError, formState } = useForm<ClientFormValues>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: defaultsFor(client),
  })
  const domainsQuery = useQuery({
    queryKey: ["admin", "domains", "picker"],
    queryFn: () => api.get<DomainPage>("/admin/domains", { query: { limit: 100 } }),
    enabled: open && !isEdit,
  })
  // Catálogo de scopes para el selector con autocompletado (multiselección filtrable).
  const [scopeQuery, setScopeQuery] = useState("")
  const scopesQuery = useQuery({
    queryKey: ["admin", "scopes", "catalog"],
    queryFn: () => api.get<Page<Scope>>("/admin/scopes", { query: { limit: 100 } }),
    enabled: open,
  })
  const allScopes = (scopesQuery.data?.data ?? []).map((s) => s.key)

  useEffect(() => {
    if (open) reset(defaultsFor(client))
  }, [open, client, reset])

  const formError = formState.errors.root?.message ?? null
  const setFormError = (message: string) => setError("root", { message })

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (isEdit) {
        await onUpdate(client.id, {
          name: values.name.trim(),
          scopes: values.scopes,
          status: values.status,
        })
      } else {
        await onCreate({
          // Vacío ⇒ null = client de instancia (acceso a todos los dominios).
          domain_id: values.domainId || null,
          name: values.name.trim(),
          scopes: values.scopes,
        })
      }
      onClose()
    } catch (error) {
      if (error instanceof ApiError) {
        const mapped = applyFieldErrors(remap(error.problem), setError)
        if (!mapped) setFormError(problemMessage(error.problem))
      } else {
        throw error
      }
    }
  })

  const domains = domainsQuery.data?.data ?? []

  return (
    <AppDrawerForm
      open={open}
      title={isEdit ? i18n.editDrawer.title : i18n.createDrawer.title}
      submitLabel={isEdit ? i18n.editDrawer.submit : i18n.createDrawer.submit}
      submitting={formState.isSubmitting}
      error={formError}
      onSubmit={onSubmit}
      onClose={onClose}
    >
      {!isEdit ? (
        <FormField
          control={control}
          name="domainId"
          label={i18n.createDrawer.domain}
          hint={isInstanceAdmin ? i18n.createDrawer.domainHint : undefined}
          required={!isInstanceAdmin}
          rules={isInstanceAdmin ? undefined : { required: i18n.validation.domainRequired }}
        >
          {(field, invalid) => (
            <Dropdown
              aria-invalid={invalid}
              selectedOptions={[field.value]}
              value={
                field.value
                  ? (domains.find((d) => d.id === field.value)?.slug ?? "")
                  : isInstanceAdmin
                    ? i18n.createDrawer.allDomains
                    : ""
              }
              onOptionSelect={(_, data) => field.onChange(data.optionValue ?? "")}
            >
              {/* Solo el admin de instancia puede crear un client sin dominio. */}
              {isInstanceAdmin ? <Option value="">{i18n.createDrawer.allDomains}</Option> : null}
              {domains.map((d) => (
                <Option key={d.id} value={d.id}>
                  {d.slug}
                </Option>
              ))}
            </Dropdown>
          )}
        </FormField>
      ) : null}

      <FormField
        control={control}
        name="name"
        label={i18n.fields.name}
        required
        rules={{ required: i18n.validation.nameRequired }}
      >
        {(field, invalid) => <Input {...field} aria-invalid={invalid} />}
      </FormField>

      <FormField
        control={control}
        name="scopes"
        label={i18n.fields.scopes}
        hint={i18n.fields.scopesHint}
      >
        {(field) => {
          const selected = field.value as string[]
          const candidates = allScopes
            .filter((s) => !selected.includes(s))
            .filter((s) => s.toLowerCase().includes(scopeQuery.trim().toLowerCase()))
          return (
            <TagPicker
              selectedOptions={selected}
              onOptionSelect={(_, data) => {
                if (data.value === NO_MATCHES) return
                field.onChange(data.selectedOptions)
                setScopeQuery("")
              }}
            >
              <TagPickerControl>
                <TagPickerGroup aria-label={i18n.fields.scopes}>
                  {selected.map((s) => (
                    <Tag key={s} shape="rounded" value={s}>
                      {s}
                    </Tag>
                  ))}
                </TagPickerGroup>
                <TagPickerInput
                  aria-label={i18n.fields.scopes}
                  value={scopeQuery}
                  onChange={(e) => setScopeQuery(e.target.value)}
                  placeholder={i18n.fields.scopesPlaceholder}
                />
              </TagPickerControl>
              <TagPickerList>
                {candidates.length > 0 ? (
                  candidates.map((s) => (
                    <TagPickerOption key={s} value={s}>
                      {s}
                    </TagPickerOption>
                  ))
                ) : (
                  <TagPickerOption value={NO_MATCHES}>
                    {scopesQuery.isPending ? i18n.fields.scopesLoading : i18n.fields.scopesNoMatch}
                  </TagPickerOption>
                )}
              </TagPickerList>
            </TagPicker>
          )
        }}
      </FormField>

      {isEdit ? (
        <FormField control={control} name="status" label={i18n.fields.status}>
          {(field) => (
            <Dropdown
              selectedOptions={[field.value]}
              value={field.value === "disabled" ? i18n.status.disabled : i18n.status.active}
              onOptionSelect={(_, data) =>
                field.onChange((data.optionValue as ApiClientStatus) ?? "active")
              }
            >
              <Option value="active">{i18n.status.active}</Option>
              <Option value="disabled">{i18n.status.disabled}</Option>
            </Dropdown>
          )}
        </FormField>
      ) : null}
    </AppDrawerForm>
  )
}

function remap(problem: ProblemDetails): ProblemDetails {
  if (!problem.errors) return problem
  const map: Record<string, string> = { domain_id: "domainId" }
  return {
    ...problem,
    errors: problem.errors.map((e) => ({ ...e, field: map[e.field] ?? e.field })),
  }
}
